// Angular
import { ApplicationRef, Component, ComponentRef, createComponent, inject, Injector, Input, OnDestroy, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

// SyncFusion
import { RemoveEventArgs, SelectEventArgs, SelectingEventArgs, TabAnimationSettingsModel, TabComponent, TabItemModel } from '@syncfusion/ej2-angular-navigations';

// Platform
import { CerFormPaneType } from '@form/cer-form-panes/cer-form-panes.service';
import { CerFormViewComponent } from '@form/cer-form-view-type/cer-form-view.component';
import { CerFormPaneView } from '@form/cer-form-panes/cer-form-pane/cer-form-pane-view';
import { CerFormService } from '@form/cer-form.service';

export type CerFormPaneEventCommandType = 'created' | 'add' | 'selecting' | 'select' | 'removing' | 'remove' | 'resize';

export class CerFormPaneViewEvent {
  public command: CerFormPaneEventCommandType;
  public paneView: CerFormPaneView;
  public pane: CerFormPaneType;
  public paneComponent?: CerFormPaneComponent;
  public viewPrevious?: CerFormPaneView;
}

@Component({
  selector: 'cer-form-pane',
  templateUrl: './cer-form-pane.component.html',
  styleUrls: ['./cer-form-pane.component.css'],
  providers: []
})

export class CerFormPaneComponent implements OnDestroy {

  @Input('pane') pane: CerFormPaneType;
  @Input('hideSingleTab') hideSingleTab: boolean = true;

  constructor(private injector: Injector, private formService: CerFormService) { }

  private appRef = inject(ApplicationRef);
  private subscriptionManager$: Subscription = new Subscription();

  private componentRefs: ComponentRef<CerFormViewComponent>[] = [];

  ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
    this.componentRefs.forEach(c => c.destroy());
    this.componentRefs = [];
  }

  public onPaneEvent(evt: CerFormPaneViewEvent): void {
    switch (evt.command) {
      case 'add':
        this.viewAdd(evt.paneView);
        break;
      case 'select':
        this.viewSelect(evt.paneView);
        break;
      case 'remove':
        this.viewRemove(evt.paneView, true, true);
        break;
      //      case 'viewsRemoveChildren':
      //        this.viewRemoveChildren(evt.view, true, true);
      //        break;
    }
  }

  public isEmpty() : boolean {
    return this.views == null || this.views.length == 0;
  }
  
  public event$: Subject<CerFormPaneViewEvent> = new Subject<CerFormPaneViewEvent>();
  private eventEmit(command: CerFormPaneEventCommandType, view: CerFormPaneView, viewPrevious: CerFormPaneView = null) {
    var event = new CerFormPaneViewEvent();
    event.command = command;
    event.paneView = view;
    event.viewPrevious;
    event.pane = this.pane;
    event.paneComponent = this;
    this.event$.next(event);
  }

  private viewSelect(view: CerFormPaneView) {
    const idx = this.views.findIndex(v => v.id == view.id);
    if (idx != null) {
      this.tabSelect(idx);
    }
  }

  // View
  private viewGetById(id: string) {
    return this.views.find(v => v.id == id);
  }

  // Pane views
  protected views: CerFormPaneView[] = [];

  public viewsAdd(views: CerFormPaneView[]) {
    views?.filter(v => v.visible == true)
      .forEach(v => this.viewAdd(v));
  }

  public viewAdd(view: CerFormPaneView) {
    if (view.visible == true) {
      var v = this.views.find(v => v == view);

      if (v == null) {
        this.views.push(view);
        this.tabAdd(view);
      }
      else if (v.activate) {
        this.tabActivate(view);
      }
    }
  }

  private viewRemoveByIdx(idx: number, removeTab: boolean) {
    var paneView: CerFormPaneView = this.views[idx];
    if (paneView != null) {
      this.viewRemove(paneView, removeTab);
    }
  }

  private viewRemove(view: CerFormPaneView, removeTab: boolean, disableTrigger: boolean = false) {
    const idx = this.views.findIndex(v => v.id == view.id);
    if (idx >= 0) {
      this.views.splice(idx, 1);    
      this.setTabCssClass();
      if (removeTab) {
        this.tabRemove(idx);
      }
      if (!disableTrigger) {
        this.eventEmit('remove', view);
      }
    }
  }

  // Tab reference
  private tabComponent: TabComponent = null;
  @ViewChild(TabComponent, { static: false }) set setViewChildTab(tabComponent: TabComponent) {
    if (tabComponent && this.tabComponent != tabComponent) {
      this.tabComponent = tabComponent;
      this.views.forEach(v => this.tabAdd(v));
    }
  }
  protected animation: TabAnimationSettingsModel = { previous: { effect: 'None' }, next: { effect: 'None' } };

  // Tab events
  protected onTabCreated() {
    if (this.tabComponent != null) {
      var tab: any = this.tabComponent;
      var idx = tab.selectedItem;
      if (tab.items?.length > idx) {
        var id: string = (<TabItemModel>tab.items[idx])?.id;
        var selectedContent: HTMLElement = <HTMLElement>(<HTMLElement>tab.content)?.children?.item(idx);
        this.tabContentValidate(id, selectedContent);
      }
    }
  }

  protected onTabSelecting(args: SelectingEventArgs) {
    var pvPrevious = args.previousIndex ? this.views[args.previousIndex] : null;
    var pv = args.selectedIndex ? this.views[args.selectedIndex] : null;
    this.eventEmit('selecting', pv, pvPrevious);
  }

  protected onTabSelect(args: SelectEventArgs) {
    const id = args.selectedItem?.attributes?.getNamedItem('data-id')?.value;
    if (this.tabContentValidate(id, args.selectedContent) == null) {
      this.onTabContentSelected(id);
    };
  }

  protected onTabRemoving(args: RemoveEventArgs) {
    var paneView: CerFormPaneView = this.views[args.removedIndex];
    if (paneView != null) {
      this.eventEmit('removing', paneView);
    }
  }

  protected onTabRemoved(args: RemoveEventArgs) {
    this.viewRemoveByIdx(args.removedIndex, false);
    this.adjustHeight(true);
    const id = args.removedItem?.attributes?.getNamedItem('data-id')?.value;
    if (id) {
      this.tabContentRemoveById(id);
    }
  }

  // Tab methods
  private tabRemove(idx: number) {
    if (idx != null) {
      this.tabComponent?.removeTab(idx);
    }
  }

  private tabAdd(paneView: CerFormPaneView) {
    this.setTabCssClass();
    if (this.tabComponent) {
      const item: TabItemModel = {
        id: paneView.id,
        header: { text: paneView.title, iconPosition: 'left' },
        visible: paneView.visible ?? true,
        content: paneView.content ?? '<div id="' + paneView.id + '" class="cer-flex-parent e-view-projection" data-id="' + paneView.id + '"></div>',
        cssClass: (paneView.mandatory == true) ? 'e-mandatory' : ''
      };
      const tab: any = this.tabComponent;
      if (tab.btnCls == null) {
        // TabControl is not rendered.
        tab.items.push(item);
      }
      else {
        // "addTab" only works after rendering
        this.tabComponent.addTab([item], tab.items.length);
        this.adjustHeight();
        if (paneView.activate) {
          this.tabSelect(tab.items.length - 1);
        }
      }
    }
  }

  private tabActivate(paneView: CerFormPaneView) {
    if ((this.tabComponent as any)?.btnCls != null) {
      this.viewSelect(paneView);
    }
  }

  protected tabCssClass: string = ''; //'e-fill';
  private setTabCssClass() {
    this.hideFirstMandatory = (this.hideSingleTab) &&
      (this.views.length == 1) &&
      (this.views.filter(v => v.mandatory == true).length == 1);
    this.tabCssClass = this.hideFirstMandatory ? this.tabCssClassHideSingleItem : '';
  }

  private hideFirstMandatory: boolean;
  private tabCssClassHideSingleItem = 'e-tab-single-item-hide';

  private adjustHeight(force: boolean = false) {
    // Adjust content fill height, when tab is shown
    const tab: any = this.tabComponent;
    const element: HTMLElement = tab?.element;
    const cntEle: HTMLElement = tab?.cntEle;
    if (element != null) {
      if (this.hideFirstMandatory) {
        element.classList.add(this.tabCssClassHideSingleItem);
      }
      else {
        element.classList.remove(this.tabCssClassHideSingleItem);
      }
    }
    const heightBefore = cntEle?.clientHeight;
    tab.setContentHeight();
    const heightAfter = cntEle?.clientHeight;
    if (heightBefore != heightAfter) {
      this.views.forEach(view => this.eventEmit('resize', view));
    }
  }

  private tabSelect(idx: number) {
    this.tabComponent?.select(idx);
  }

  private tabContentValidate(id: string, selectedContent: HTMLElement): ComponentRef<CerFormViewComponent> {
    if (id?.length > 0 && selectedContent != null) {
      const contentAnchor = CerFormPaneComponent.tabContentAnchorGet(selectedContent, id);
      if (contentAnchor != null) {
        const view = this.viewGetById(id);
        if (view != null) {
          return this.tabContentAdd(view, selectedContent, contentAnchor);
        }
      }
    }
    return null;
  }

  private static tabContentAnchorGet(content: HTMLElement, id: string) {
    if (content != null && content?.childElementCount == 1 && content.firstElementChild instanceof HTMLDivElement) {
      const contentChild = content.firstElementChild;
      if (contentChild != null && contentChild?.childElementCount == 1 && contentChild.firstElementChild instanceof HTMLDivElement) {
        const elementChild = <HTMLDivElement>contentChild.firstElementChild;
        if (elementChild?.attributes.getNamedItem('data-id')?.value == id) {
          if (elementChild.classList.contains('e-view-projection')) {
            elementChild.classList.remove('e-view-projection');
            return elementChild;
          }
        }
      }
    }
    return null;
  }

  // Content
  private onTabContentSelected(id: string) {
    if (id?.length > 0) {
      this.componentRefs.filter(c => c.instance.view.id == id).forEach(
        c => c.instance.select()
      );
    }
  }

  private tabContentAdd(view: CerFormPaneView, content: HTMLElement, hostElement: HTMLDivElement): ComponentRef<CerFormViewComponent> {

    const appInjector = this.appRef.injector;
    const componentRef = createComponent(CerFormViewComponent, { hostElement: hostElement, elementInjector: this.injector, environmentInjector: appInjector });
    componentRef.instance.view = view.view;

    this.appRef.attachView(componentRef.hostView);
    this.componentRefs.push(componentRef);

    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  private tabContentRemoveById(id: string) {
    this.componentRefs
      .forEach((c, idx) => {
        const view = c.instance?.view;
        if (view?.id == id) {
          view.appComponent = null;
          this.componentRefs.splice(idx, 1)
          c.destroy();
        }
      });
  }
}